import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowUpCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  RotateCcw,
  Shield,
  Clock,
  Settings,
  Lock,
  KeyRound,
  Play,
  Terminal,
  Server,
  Database,
  Globe,
  Activity,
  Eye,
  EyeOff,
  GitBranch,
  History,
  Package,
} from 'lucide-react';
import api from '../lib/api';

function StatusPulse({ status }) {
  const colors = {
    idle: 'bg-slate-400',
    running: 'bg-amber-500',
    success: 'bg-emerald-500',
    failed: 'bg-rose-500',
    service_unreachable: 'bg-slate-400',
    not_configured: 'bg-slate-400',
  };
  const isAnimated = status === 'running';
  const color = colors[status] || 'bg-slate-400';

  return (
    <span className="relative flex h-3 w-3" data-testid="status-pulse">
      {isAnimated && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-75`} />}
      <span className={`relative inline-flex rounded-full h-3 w-3 ${color}`} />
    </span>
  );
}

function UpdateStep({ text, index, total }) {
  const isLast = index === total - 1;
  let icon = <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />;
  if (text?.toLowerCase().includes('error') || text?.toLowerCase().includes('fail')) {
    icon = <XCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />;
  } else if (text?.toLowerCase().includes('rollback')) {
    icon = <RotateCcw className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />;
  } else if (isLast) {
    icon = <Loader2 className="w-3.5 h-3.5 text-primary animate-spin flex-shrink-0" />;
  }

  return (
    <div className="flex items-start gap-2 py-1.5 font-mono text-xs text-foreground/80" data-testid={`update-step-${index}`}>
      {icon}
      <span>{text}</span>
    </div>
  );
}

function HistoryEvent({ event }) {
  const statusColors = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    failed: 'border-rose-200 bg-rose-50 text-rose-700',
    pending: 'border-amber-200 bg-amber-50 text-amber-700',
    running: 'border-blue-200 bg-blue-50 text-blue-700',
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-0" data-testid="history-event">
      <div className={`p-1 rounded-full mt-0.5 ${event.action === 'update_triggered' ? 'bg-primary/10' : 'bg-muted'}`}>
        {event.action === 'update_triggered' ? (
          <ArrowUpCircle className="w-4 h-4 text-primary" />
        ) : (
          <RotateCcw className="w-4 h-4 text-amber-600" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground">
            {event.action === 'update_triggered' ? 'Update Triggered' :
             event.action === 'manual_rollback' ? 'Manual Rollback' : event.action}
          </span>
          {event.status && (
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusColors[event.status] || ''}`}>
              {event.status}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {event.performed_by_email || event.performed_by}
          {event.created_at && (
            <span className="ml-2 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(event.created_at).toLocaleString()}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

export default function DeploymentDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsData, setSettingsData] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const pollingRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [healthRes, statusRes, historyRes, snapshotsRes] = await Promise.all([
        api.get('/admin/health-summary'),
        api.get('/admin/update-status'),
        api.get('/admin/deployment-history'),
        api.get('/admin/rollback-snapshots').catch(() => ({ data: { snapshots: [] } })),
      ]);
      setHealthData(healthRes.data);
      setUpdateStatus(statusRes.data);
      setHistory(historyRes.data || []);
      setSnapshots(snapshotsRes.data?.snapshots || []);
    } catch (err) {
      console.error('Failed to load deployment data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/admin/deployment-settings');
      setSettingsData(res.data || {});
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (updateStatus?.status === 'running') {
      pollingRef.current = setInterval(loadData, 3000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [updateStatus?.status, loadData]);

  const triggerUpdate = async () => {
    setShowConfirm(false);
    try {
      await api.post('/admin/trigger-update');
      toast.success('Update triggered — monitoring progress...');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to trigger update');
    }
  };

  const loadSnapshots = async () => {
    setSnapshotsLoading(true);
    try {
      const res = await api.get('/admin/rollback-snapshots');
      setSnapshots(res.data.snapshots || []);
    } catch (err) {
      console.error('Failed to load snapshots', err);
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const triggerRollback = async (snapshotId) => {
    setRollingBack(true);
    setShowRollbackConfirm(null);
    try {
      await api.post('/admin/trigger-rollback', { snapshot_id: snapshotId });
      toast.success('Rollback triggered — monitoring progress...');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to trigger rollback');
    } finally {
      setRollingBack(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post('/admin/deployment-settings', {
        update_secret: settingsData.update_secret,
        update_service_url: settingsData.update_service_url,
      });
      toast.success('Settings saved');
      setShowSettings(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // P3 SEC hardening — client-side developer-password gate removed.
  // Every mutating endpoint (routes/deployment.py) requires super_admin
  // server-side. Non-privileged users see a role-based denial screen.
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="system-update-gate">
        <div className="rounded-full p-4 bg-muted border mb-4">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">System Update Console</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          This section is restricted to super administrators.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isRunning = updateStatus?.status === 'running';
  const isConfigured = healthData?.update_service_configured || false;
  const updateSteps = updateStatus?.output || [];

  const statusLabel = {
    idle: 'Ready',
    running: 'Updating...',
    success: 'Last update successful',
    failed: updateStatus?.rolled_back ? 'Failed — Rolled back' : 'Failed',
    not_configured: 'Not configured',
    service_unreachable: 'Service offline',
  }[updateStatus?.status] || 'Unknown';

  const statusColor = {
    idle: 'text-muted-foreground',
    running: 'text-amber-600',
    success: 'text-emerald-600',
    failed: 'text-destructive',
    not_configured: 'text-muted-foreground',
    service_unreachable: 'text-muted-foreground',
  }[updateStatus?.status] || 'text-muted-foreground';

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="system-update-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 border border-primary/20">
              <ArrowUpCircle className="w-6 h-6 text-primary" />
            </div>
            System Update
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Stable rolling updates with automatic rollback
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadSettings(); setShowSettings(true); }}
            data-testid="btn-update-settings"
          >
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Update Control */}
      <Card data-testid="update-control-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <StatusPulse status={updateStatus?.status} />
              <div>
                <p className={`text-sm font-semibold ${statusColor}`}>{statusLabel}</p>
                {updateStatus?.started_at && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Started: {new Date(updateStatus.started_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={isRunning || !isConfigured}
              data-testid="btn-trigger-update"
            >
              {isRunning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Deploy Update</>
              )}
            </Button>
          </div>

          {!isConfigured && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 mb-4" data-testid="config-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-amber-800 font-medium">Update service not configured</p>
                <p className="text-xs text-amber-600 mt-1">
                  Click <strong>Settings</strong> to configure the update secret. The host-side update service must be running on your server.
                </p>
              </div>
            </div>
          )}

          {/* Update flow */}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground py-3 border-t overflow-x-auto">
            <span className="flex items-center gap-1 whitespace-nowrap"><Server className="w-3 h-3" /> git pull</span>
            <span>&#8594;</span>
            <span className="whitespace-nowrap">docker build</span>
            <span>&#8594;</span>
            <span className="whitespace-nowrap">snapshot rollback</span>
            <span>&#8594;</span>
            <span className="whitespace-nowrap">start new</span>
            <span>&#8594;</span>
            <span className="flex items-center gap-1 whitespace-nowrap"><Activity className="w-3 h-3" /> health check</span>
            <span>&#8594;</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-emerald-600"><CheckCircle2 className="w-3 h-3" /> live</span>
            <span className="mx-1">|</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-amber-600"><RotateCcw className="w-3 h-3" /> rollback</span>
          </div>

          {/* Progress log */}
          {updateSteps.length > 0 && (
            <div className="mt-4 rounded-lg bg-slate-900 border border-slate-700 p-4" data-testid="update-progress-log">
              <div className="flex items-center gap-2 mb-3">
                <Terminal className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Update Progress</span>
              </div>
              <div className="max-h-[400px] overflow-y-auto pr-2" style={{scrollbarWidth: 'thin', scrollbarColor: '#475569 #1e293b'}}>
                {updateSteps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2 py-1.5 font-mono text-xs text-slate-300" data-testid={`update-step-${i}`}>
                    {i === updateSteps.length - 1 && isRunning ? (
                      <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
                    ) : step?.toLowerCase().includes('failed') || step?.toLowerCase().includes('error') || step?.startsWith('ROLLBACK') || step?.startsWith('FAILED') ? (
                      <XCircle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {updateStatus?.error && (
            <div className="mt-4 flex items-start gap-3 p-4 rounded-lg bg-rose-50 border border-rose-200" data-testid="update-error">
              <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-rose-800 font-medium">
                  {updateStatus.rolled_back ? 'Update failed — automatically rolled back' : 'Update failed'}
                </p>
                <p className="text-xs text-rose-600 mt-1 font-mono">{updateStatus.error}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="health-card-backend">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
                <Server className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold">Backend API</p>
                <p className="text-xs text-muted-foreground">FastAPI + Uvicorn</p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Healthy
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="health-card-database">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-violet-50 border border-violet-200">
                <Database className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <p className="text-sm font-semibold">MongoDB</p>
                <p className="text-xs text-muted-foreground">Database engine</p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="health-card-frontend">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200">
                <Globe className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-semibold">Frontend</p>
                <p className="text-xs text-muted-foreground">React + Nginx</p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Serving
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Rollback Snapshots */}
      <Card data-testid="rollback-snapshots-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="w-5 h-5 text-primary" />
                Rollback Snapshots
              </CardTitle>
              <CardDescription>
                Saved versions of your application. You can manually roll back to any snapshot below.
                Up to 2 snapshots are retained.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadSnapshots} disabled={snapshotsLoading || !isConfigured} data-testid="btn-refresh-snapshots">
              {snapshotsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isConfigured ? (
            <div className="text-center py-8">
              <Settings className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Configure the update service to use snapshots</p>
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">No snapshots available yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Snapshots are created automatically before each update</p>
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.map((snap, i) => (
                <div
                  key={snap.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors"
                  data-testid={`snapshot-${snap.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${i === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
                      <Package className={`w-5 h-5 ${i === 0 ? 'text-emerald-600' : 'text-slate-500'}`} />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">Snapshot {snap.id}</span>
                        {i === 0 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-300 text-emerald-700 bg-emerald-50">
                            Latest
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {snap.label || 'pre-update'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {snap.created_at ? new Date(snap.created_at).toLocaleString() : 'Unknown'}
                        </span>
                        {snap.git_commit && (
                          <span className="flex items-center gap-1 font-mono">
                            <GitBranch className="w-3 h-3" />
                            {snap.git_branch || 'main'} @ {snap.git_commit}
                          </span>
                        )}
                      </div>
                      {snap.git_message && (
                        <p className="text-xs text-muted-foreground/70 font-mono truncate max-w-md">
                          {snap.git_message}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground">
                        Services: {(snap.services || ['backend', 'frontend']).join(', ')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRollbackConfirm(snap)}
                    disabled={isRunning || rollingBack}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid={`btn-rollback-${snap.id}`}
                  >
                    {rollingBack ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-1" />
                    )}
                    Rollback
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update History */}
      <Card data-testid="update-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-5 h-5 text-primary" />
            Update History
          </CardTitle>
          <CardDescription>
            Recent deployment events and rollback operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <ScrollArea className="max-h-[350px]">
              {history.map((event, i) => (
                <HistoryEvent key={i} event={event} />
              ))}
            </ScrollArea>
          ) : (
            <div className="text-center py-10">
              <Clock className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground text-sm">No update events yet</p>
              <p className="text-muted-foreground/70 text-xs mt-1">Events will appear here when you deploy updates</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-update-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Confirm Production Update
            </DialogTitle>
            <DialogDescription>
              This will pull the latest code, build new containers, and deploy them.
              If health checks fail, the system will automatically roll back.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">Old containers keep running during build</p>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">Health checks verify new version before going live</p>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border">
              <RotateCcw className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">Automatic rollback if new version fails</p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="btn-cancel-update">
              Cancel
            </Button>
            <Button onClick={triggerUpdate} data-testid="btn-confirm-update">
              <ArrowUpCircle className="w-4 h-4 mr-2" /> Deploy Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg" data-testid="update-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-muted-foreground" />
              Update Service Configuration
            </DialogTitle>
            <DialogDescription>
              Configure the connection to the host-side update service.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <label className="text-sm font-medium block mb-1.5">Update Secret</label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={settingsData.update_secret || ''}
                  onChange={(e) => setSettingsData(d => ({ ...d, update_secret: e.target.value }))}
                  placeholder="Paste the UPDATE_SECRET from your server"
                  className="pr-10"
                  data-testid="input-update-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Must match the UPDATE_SECRET on your production server
              </p>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Service URL</label>
              <Input
                value={settingsData.update_service_url || ''}
                onChange={(e) => setSettingsData(d => ({ ...d, update_service_url: e.target.value }))}
                placeholder="http://host.docker.internal:9090"
                data-testid="input-service-url"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Default: http://host.docker.internal:9090
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={saveSettings} disabled={savingSettings} data-testid="btn-save-settings">
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirm Dialog */}
      <Dialog open={!!showRollbackConfirm} onOpenChange={() => setShowRollbackConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-rollback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RotateCcw className="w-5 h-5" />
              Confirm Manual Rollback
            </DialogTitle>
            <DialogDescription>
              This will restore your application to a previous version. A snapshot of the current state will be saved first.
            </DialogDescription>
          </DialogHeader>
          {showRollbackConfirm && (
            <div className="space-y-3 py-3">
              <div className="p-3 rounded-lg bg-muted/50 border space-y-2">
                <p className="text-sm font-medium">Rolling back to:</p>
                <div className="text-xs space-y-1 text-muted-foreground">
                  <p><strong>Snapshot:</strong> {showRollbackConfirm.id}</p>
                  <p><strong>Created:</strong> {new Date(showRollbackConfirm.created_at).toLocaleString()}</p>
                  {showRollbackConfirm.git_commit && (
                    <p><strong>Commit:</strong> <span className="font-mono">{showRollbackConfirm.git_branch} @ {showRollbackConfirm.git_commit}</span></p>
                  )}
                  {showRollbackConfirm.git_message && (
                    <p><strong>Message:</strong> {showRollbackConfirm.git_message}</p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  Your current running version will be saved as a snapshot before rollback, so you can undo this if needed.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowRollbackConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => triggerRollback(showRollbackConfirm.id)}
              disabled={rollingBack}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="btn-confirm-rollback"
            >
              {rollingBack ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rolling back...</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-2" /> Rollback Now</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
